import canonicalJson from "canonical-json";

import { FeatureTargeting } from "@reflag/shared/featureTargetingAPI";

type TargetingComparable = Pick<
  FeatureTargeting,
  "targetingMode" | "segmentIds" | "companyIds" | "userIds" | "customRules"
>;

function diffStringArrays(oldValues: string[], newValues: string[]) {
  const oldSet = new Set(oldValues);
  const newSet = new Set(newValues);

  const added = [...newSet].filter((value) => !oldSet.has(value));
  const removed = [...oldSet].filter((value) => !newSet.has(value));

  return { added, removed };
}

function getNormalizedTargeting(targeting: TargetingComparable) {
  if (targeting.targetingMode === "none") {
    return {
      companyIds: [] as string[],
      userIds: [] as string[],
      segmentIds: [] as string[],
      customRules: [] as string[],
    };
  }

  return {
    companyIds: targeting.companyIds,
    userIds: targeting.userIds,
    segmentIds: targeting.segmentIds,
    customRules: targeting.customRules.map((rule) => canonicalJson(rule)),
  };
}

export function hasTargetingChanged(
  currentTargeting: TargetingComparable,
  previousTargeting: TargetingComparable,
) {
  const everyoneModeChanged =
    (currentTargeting.targetingMode === "everyone" ||
      previousTargeting.targetingMode === "everyone") &&
    currentTargeting.targetingMode !== previousTargeting.targetingMode;

  const previousNormalized = getNormalizedTargeting(previousTargeting);
  const currentNormalized = getNormalizedTargeting(currentTargeting);

  const companiesDiff = diffStringArrays(
    previousNormalized.companyIds,
    currentNormalized.companyIds,
  );
  const usersDiff = diffStringArrays(
    previousNormalized.userIds,
    currentNormalized.userIds,
  );
  const segmentsDiff = diffStringArrays(
    previousNormalized.segmentIds,
    currentNormalized.segmentIds,
  );
  const rulesDiff = diffStringArrays(
    previousNormalized.customRules,
    currentNormalized.customRules,
  );

  return Boolean(
    everyoneModeChanged ||
    companiesDiff.added.length ||
    companiesDiff.removed.length ||
    usersDiff.added.length ||
    usersDiff.removed.length ||
    segmentsDiff.added.length ||
    segmentsDiff.removed.length ||
    rulesDiff.added.length ||
    rulesDiff.removed.length,
  );
}
